import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  companyIdSelector,
  preGlobalSearchCalendarAtom,
  RideCrudModeKey,
  storeTopCalendarData,
  topCalendarDatesAtomFamily,
  topCalendarModeAtomFamily,
} from "@ryde/shared";

/**
 * Global ride-id search temporarily overrides the top calendar so results are not
 * clipped by the date filter. Re-init from URL cannot undo that: after search,
 * date1/date2 in the query string already hold the overridden ride range.
 * We snapshot dates+mode once before override and restore them on search clear.
 */
export const usePreGlobalSearchCalendar = () => {
  const companyId = useRecoilValue(companyIdSelector);
  const [snapshot, setSnapshot] = useRecoilState(preGlobalSearchCalendarAtom);
  const currentDates = useRecoilValue(
    topCalendarDatesAtomFamily(RideCrudModeKey.uiUnit),
  );
  const currentMode = useRecoilValue(
    topCalendarModeAtomFamily(RideCrudModeKey.uiUnit),
  );
  const setTopCalendarDates = useSetRecoilState(
    topCalendarDatesAtomFamily(RideCrudModeKey.uiUnit),
  );
  const setCalendarMode = useSetRecoilState(
    topCalendarModeAtomFamily(RideCrudModeKey.uiUnit),
  );

  // Keep the first pre-search range if the user runs another id search before clear.
  const saveSnapshotIfNeeded = useEvent((): void => {
    if (snapshot || !currentDates) {
      return;
    }

    setSnapshot({
      dates: currentDates,
      mode: currentMode,
    });
  });

  const restoreSnapshotIfNeeded = useEvent((): void => {
    if (!snapshot || !companyId) {
      return;
    }

    setTopCalendarDates(snapshot.dates);
    setCalendarMode(snapshot.mode);
    // Keep session storage in sync so a later page reload matches the restored range.
    storeTopCalendarData({
      companyId,
      entity: RideCrudModeKey.uiUnit,
      dates: snapshot.dates,
      mode: snapshot.mode,
    });
    setSnapshot(undefined);
  });

  return {
    saveSnapshotIfNeeded,
    restoreSnapshotIfNeeded,
  };
};
